import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { first, map, startWith, Subject, Subscription, takeUntil } from 'rxjs';
import {
  MapFilterItem,
  MapFilterType,
  MapFilterValueType,
} from 'app/shared/components/main-map/components/search-results/map-filter-state.model';
import { typeObjectFieldName } from 'app/shared/constants/search-results.constant';
import { CodedValueItem } from 'app/shared/interfaces/coded-value-item.interface';
import { EsriUtils } from 'app/shared/utils/esri.utils';
import { MapFilterManager } from '../../map-filter.manager';

@Component({
  selector: 'app-object-type-filter-form',
  templateUrl: 'object-type-filter-form.component.html',
  styleUrls: ['object-type-filter-form.component.scss'],
})
export class ObjectTypeFilterFormComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input()
  visible: boolean;
  @Output()
  visibleChange = new EventEmitter<boolean>();

  @Input()
  mainMapImageLayer: __esri.MapImageLayer;

  objectTypeList: CodedValueItem<number>[];

  private _isAllSelected = false;

  get isAllSelected() {
    return this._isAllSelected;
  }

  set isAllSelected(value) {
    value = !!value;
    if (this._isAllSelected !== value) {
      this._isAllSelected = value;

      this.objectTypeList?.forEach((item) => {
        this.setObjectTypeSelected(item, value);
      });

      this.handleAllSelectedItemRef();
    }
  }

  private filterList: MapFilterItem[] = [];
  private newFiltersMap = new Map<
    number | string,
    CodedValueItem<number | string>
  >();
  private filterKeySetRemoved = new Set<number | string>();

  private initialItemsSelectedSet = new Set<number | string>();
  private itemsSelectedSet = new Set<number | string>();

  private filtersSubscription: Subscription;

  private objectTypeListSubscription: Subscription;

  private lifecycle = new Subject<void>();

  constructor(private mapFilterManager: MapFilterManager) {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mainMapImageLayer) {
      this.handleObjectTypeList();
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  onShowingInternal() {
    this.filtersSubscription?.unsubscribe();
    this.filtersSubscription = this.mapFilterManager.onFiltersChanged
      .pipe(
        takeUntil(this.visibleChange.pipe(first((x) => !x))),
        takeUntil(this.lifecycle)
      )
      .subscribe((list) => {
        this.filterList = list.filter(
          (x) => x.type === MapFilterType.ObjectType
        );

        this.handleValues();
      });
  }

  onHiddenInternal() {
    this.filtersSubscription?.unsubscribe();

    this.newFiltersMap.clear();
    this.filterKeySetRemoved.clear();
    this.itemsSelectedSet.clear();
    this.initialItemsSelectedSet.clear();
    this.isAllSelected = false;
  }

  onBtnApplyClick() {
    let adds: MapFilterItem[];
    let deletesAux: (number | string)[];

    if (this.isAllSelected) {
      if (
        !this.filterList?.some(
          (x) => x.value === MapFilterItem.DEFAULT_VALUE_ALL
        )
      ) {
        adds = [
          new MapFilterItem({
            type: MapFilterType.ObjectType,
            text: 'Todos',
            field: typeObjectFieldName,
            valueAsStr: MapFilterItem.DEFAULT_VALUE_ALL,
            valueType: MapFilterValueType.String,
          }),
        ];
      }

      deletesAux = Array.from(this.initialItemsSelectedSet.values());
    } else {
      adds = Array.from(this.newFiltersMap.values(), (item) => {
        return new MapFilterItem({
          type: MapFilterType.ObjectType,
          text: item.label,
          field: typeObjectFieldName,
          valueAsStr: MapFilterItem.stringfyValue(item.value),
          valueType: MapFilterItem.getValueType(item.value),
        });
      });
      deletesAux = Array.from(this.filterKeySetRemoved.values());
    }

    const deletes = deletesAux
      ?.map((value) => {
        return this.mapFilterManager.getFilterKeyBy({
          type: MapFilterType.ObjectType,
          field: typeObjectFieldName,
          valueAsStr: MapFilterItem.stringfyValue(value),
          valueType: MapFilterItem.getValueType(value),
        });
      })
      ?.filter((x) => !!x);

    this.mapFilterManager.applyEditsFilters({
      adds,
      deletes,
    });

    this.visible = false;
    this.visibleChange.emit(this.visible);
  }

  onBtnClearClick() {
    this.resetValues();
  }

  isObjectTypeSelected(item: CodedValueItem<number>): boolean {
    return this.itemsSelectedSet.has(item.value);
  }

  setObjectTypeSelected(
    item: CodedValueItem<number | string>,
    selected: boolean
  ) {
    const key = item.value;
    if (selected) {
      this.itemsSelectedSet.add(key);
      this.filterKeySetRemoved.delete(key);

      if (!this.initialItemsSelectedSet.has(key)) {
        this.newFiltersMap.set(key, item);
      }
    } else {
      this.itemsSelectedSet.delete(key);

      if (this.initialItemsSelectedSet.has(key)) {
        this.filterKeySetRemoved.add(key);
      }

      this.newFiltersMap.delete(key);

      if (this.isAllSelected) {
        this._isAllSelected = false;
        this.handleAllSelectedItemRef();
      }
    }
  }

  private handleAllSelectedItemRef() {
    const itemRef = this.filterList?.find(
      (x) => x.value === MapFilterItem.DEFAULT_VALUE_ALL
    );
    if (itemRef) {
      this.setObjectTypeSelected(itemRef as any, this.isAllSelected);
    }
  }

  private handleValues() {
    this.filterKeySetRemoved.clear();
    this.newFiltersMap.clear();

    this.initialItemsSelectedSet = new Set(
      this.filterList?.map((x) => {
        return x.value;
      }) ?? []
    );
    this.itemsSelectedSet = new Set(this.initialItemsSelectedSet);

    this._isAllSelected = false;
    this.isAllSelected = this.filterList?.some(
      (x) => x.value === MapFilterItem.DEFAULT_VALUE_ALL
    );
  }

  private resetValues() {
    this.newFiltersMap.clear();
    this.filterKeySetRemoved = new Set(this.initialItemsSelectedSet);

    this.itemsSelectedSet.clear();
    this.isAllSelected = false;
  }

  private handleObjectTypeList() {
    this.objectTypeListSubscription?.unsubscribe();
    this.objectTypeList = null;
    if (!this.mainMapImageLayer) {
      return;
    }

    const collection = this.mainMapImageLayer.allSublayers;

    const getList = () => {
      return collection
        .toArray()
        .reverse()
        .map<CodedValueItem<number>>((layer) => {
          return {
            label: layer.title,
            value: layer.id,
          };
        });
    };

    this.objectTypeListSubscription = EsriUtils.on(collection, 'change')
      .pipe(
        map(() => getList()),
        startWith(getList()),
        takeUntil(this.lifecycle)
      )
      .subscribe((list) => {
        this.objectTypeList = list;
      });
  }
}
